import {
  ChangeDetectionStrategy,
  Component,
  inject,
  OnInit,
  signal,
} from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import {
  MAT_DIALOG_DATA,
  MatDialogClose,
  MatDialogRef,
} from '@angular/material/dialog';
import { finalize, Observable } from 'rxjs';
import { PresentationalAtomsModule } from '@et/presentational/atoms';
import { Profile } from '@et/typings';

export type CustodianInfoAddEditComponentData = {
  eJournalCustodianEmail: string;
  eJournalCustodianName: string;
  updateFn: (
    eJournalCustodianName: string,
    eJournalCustodianEmail: string,
  ) => Observable<Profile>;
};

@Component({
  selector: 'et-journal-custodian-info-add-edit',
  imports: [ReactiveFormsModule, PresentationalAtomsModule, MatDialogClose],
  templateUrl: './custodian-info-add-edit.component.html',
  styleUrl: './custodian-info-add-edit.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CustodianInfoAddEditComponent implements OnInit {
  private readonly fb = inject(FormBuilder);
  private readonly dialogRef = inject(
    MatDialogRef<CustodianInfoAddEditComponent>,
  );
  private readonly dialogData =
    inject<CustodianInfoAddEditComponentData>(MAT_DIALOG_DATA);

  isSubmitting = signal<boolean>(false);

  form = this.fb.nonNullable.group({
    eJournalCustodianEmail: ['', [Validators.required, Validators.email]],
    eJournalCustodianName: ['', [Validators.required]],
  });

  /** Whether the dialog was opened with existing custodian name and email to edit. */
  get isEditMode(): boolean {
    return (
      !!this.dialogData.eJournalCustodianEmail &&
      !!this.dialogData.eJournalCustodianName
    );
  }

  /**
   * Pre-fills the form when opened in edit mode with existing custodian values from dialog data.
   */
  ngOnInit(): void {
    if (this.isEditMode) {
      this.form.patchValue({
        eJournalCustodianName: this.dialogData.eJournalCustodianName,
        eJournalCustodianEmail: this.dialogData.eJournalCustodianEmail,
      });
    }
  }

  /**
   * Validates the form, persists trimmed custodian fields via the dialog `updateFn`, and closes with the updated profile.
   */
  onSubmit() {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    const { eJournalCustodianName, eJournalCustodianEmail } =
      this.form.getRawValue();

    this.isSubmitting.set(true);
    this.dialogData
      .updateFn(eJournalCustodianName.trim(), eJournalCustodianEmail.trim())
      .pipe(finalize(() => this.isSubmitting.set(false)))
      .subscribe((profile) => this.dialogRef.close(profile));
  }
}
